/**
 * T070 (001-catalogue-admin-screens, US3 §2, FR-015, D-03). Shows what a `TierPriceRow` itself
 * says about how it got its rate — `source`, `rate`, `derivedFromBaseRate` — "when asked", per
 * FR-015: mounted from `pages/tiers/prices/List.tsx`'s per-row "Attribution" toggle, so an admin
 * sees it on request rather than for all ~15,000 rows at once.
 *
 * **Does not compute the winning rule.** GAP-3 (`contracts/backend-gaps.md`) is that
 * `TierPriceRow` carries no `winningRuleId`/`matchedScope`/`appliedDiscountPct` — only what this
 * component renders. `rate ÷ derivedFromBaseRate` LOOKS like the winning discount percentage and
 * is not one: the tier's `priceRoundingMode` sits between the two numbers, so the ratio is
 * arithmetic on two already-rounded values, not the rule's own percentage (constitution I;
 * quickstart.md scenario 7 asserts this absence). The panel says so in words instead, and links to
 * the tier's rules — in `sequence` order, the order they are actually evaluated in — so precedence
 * is inspectable there rather than inferred here.
 */
import { Link } from "react-router-dom";
import { PiInfoDuotone as Info } from "react-icons/pi";
import { Alert, Text } from "rizzui";
import { Badge } from "@steelyard/theme";
import { formatMoney } from "../lib/format";
import { tierPath } from "../lib/paths";
import type { PriceSource, StaleReason } from "../lib/services/catalog";

export function RuleAttribution({
  tierSlug,
  row,
}: {
  tierSlug: string;
  row: { source: PriceSource; rate: number; derivedFromBaseRate: number | null; isStale: boolean; staleReason: StaleReason };
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Badge kind={row.source} />
        {row.isStale ? <Badge kind="stale" /> : null}
        <Text className="text-sm">
          Rate <span className="font-mono font-medium">{formatMoney(row.rate)}</span>
          {row.derivedFromBaseRate !== null ? (
            <>
              {" "}
              · derived from base rate <span className="font-mono">{formatMoney(row.derivedFromBaseRate)}</span>
            </>
          ) : null}
        </Text>
      </div>

      <Alert color="warning" variant="flat" size="sm" icon={<Info aria-hidden className="size-4" />}>
        <span className="text-sm">
          <strong className="font-semibold">Which discount rule produced this rate is not shown here.</strong> The
          API does not yet report a winning rule&rsquo;s identity on a price row (GAP-3, FR-015) —
          this app does not derive one from <span className="font-mono">rate ÷ derivedFromBaseRate</span>,
          since the tier&rsquo;s rounding mode sits between the two numbers and the result would
          not be the rule&rsquo;s real discount percentage.{" "}
          <Link to={`${tierPath(tierSlug)}/rules`} className="underline">
            See the tier&rsquo;s rules, in the order they&rsquo;re evaluated
          </Link>
          .
        </span>
      </Alert>
    </div>
  );
}
